import React, { useEffect, useState } from 'react';
import { motion, useSpring, useTransform } from 'framer-motion';
import { Button } from './ui/Button';
import { TrendingDown, PieChart } from 'lucide-react';
interface SlideMarketSizeProps {
  onContinue: () => void;
}
export function SlideMarketSize({
  onContinue
}: SlideMarketSizeProps) {
  const [stage, setStage] = useState(0);
  useEffect(() => {
    const t1 = setTimeout(() => setStage(1), 500);
    const t2 = setTimeout(() => setStage(2), 1300);
    const t3 = setTimeout(() => setStage(3), 2200);
    const t4 = setTimeout(() => setStage(4), 3000);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
      clearTimeout(t4);
    };
  }, []);
  const targetPct = 35;
  const radius = 90;
  const circumference = 2 * Math.PI * radius;
  const pctSpring = useSpring(0, {
    stiffness: 60,
    damping: 18
  });
  const dashOffset = useTransform(pctSpring, latest => circumference - circumference * latest / 100);
  const displayPct = useTransform(pctSpring, latest => `${Math.round(latest)}%`);
  useEffect(() => {
    if (stage >= 3) pctSpring.set(targetPct);
  }, [stage]);
  return <div className="h-full w-full flex flex-col px-8 py-10 md:px-20 md:py-14 max-w-7xl mx-auto" data-id="element-606" data-mp="7plpen79u7sf">
      {/* Heading */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} className="space-y-4" data-id="element-607" data-mp="3tyn113ow6wvx">
        <h2 className="text-4xl md:text-5xl font-serif text-white" data-id="element-608" data-mp="3ky8394uqrpbj">
          The Scale of the Tail
        </h2>
        <p className="text-xl text-white/60" data-id="element-609" data-mp="36a3q0gegk605">
          Legacy revenue is the engine that funds future innovation.
        </p>
      </motion.div>

      <div className="flex-1 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center mt-10" data-id="element-610" data-mp="3b8y8fh8ijsjq">
        {/* LEFT */}
        <div className="lg:col-span-7 space-y-12" data-id="element-611" data-mp="2xb065a7gvmkl">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: stage >= 2 ? 1 : 0,
          y: stage >= 2 ? 0 : 20
        }} transition={{
          duration: 0.6
        }} data-id="element-612" data-mp="326yq8llriif7">
            <div className="text-6xl md:text-8xl font-serif text-teal-300 tracking-tight leading-none" data-id="element-613" data-mp="y2rw2twnqsq0">
              30–35<span className="text-5xl md:text-7xl" data-id="element-614" data-mp="u1y8k1n4nfxn">%</span>
            </div>
            <p className="text-lg md:text-xl text-white/70 mt-4 max-w-md leading-snug" data-id="element-615" data-mp="1qpl0bm99aa4r">
              of big pharma revenue comes from{' '}
              <span className="text-white font-medium" data-id="element-616" data-mp="1lpgh3ogyr1au">
                mature / legacy brands.
              </span>
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: stage >= 3 ? 1 : 0,
          y: stage >= 3 ? 0 : 20
        }} transition={{
          duration: 0.6
        }} data-id="element-617" data-mp="1cptfp2o8hvhs">
            <div className="text-6xl md:text-8xl font-serif text-white tracking-tight leading-none" data-id="element-618" data-mp="2d0cmz5j8lp9h">
              $180B–300B<span className="text-teal-400" data-id="element-619" data-mp="2hynvncxsptq4">+</span>
            </div>
            <p className="text-lg md:text-xl text-white/70 mt-4 max-w-md leading-snug" data-id="element-620" data-mp="2lzhis43ez4hy">
              facing{' '}
              <span className="text-white font-medium" data-id="element-621" data-mp="2qr9e0rxilyef">
                loss of exclusivity
              </span>{' '}
              in the coming years.
            </p>
          </motion.div>
        </div>

        {/* RIGHT */}
        <div className="lg:col-span-5 flex items-center justify-center" data-id="element-622" data-mp="30msy8h2pv8nc">
          <motion.div initial={{
          opacity: 0,
          scale: 0.8
        }} animate={{
          opacity: stage >= 1 ? 1 : 0,
          scale: stage >= 1 ? 1 : 0.8
        }} transition={{
          duration: 0.8,
          type: 'spring'
        }} className="absolute inset-0 flex flex-col items-center justify-center text-center z-10" data-id="element-623" data-mp="35p1rzfneyeqi">
            <span className="text-6xl font-serif text-white" data-id="element-624" data-mp="fzj6uosselwd">30-35%</span>
            <span className="text-sm font-medium text-white/60 uppercase tracking-widest mt-2" data-id="element-625" data-mp="fz9b0kl29kir">
              Of Big Pharma Revenue
            </span>
          </motion.div>
          <motion.div initial={{
          opacity: 0,
          scale: 0.8
        }} animate={{
          opacity: stage >= 3 ? 1 : 0,
          scale: stage >= 3 ? 1 : 0.8
        }} transition={{
          duration: 0.6
        }} className="relative" data-id="element-626" data-mp="p1ionjbmlu0d">
            <svg width="280" height="280" viewBox="0 0 240 240" className="overflow-visible" data-id="element-627" data-mp="69pwfr4dsctp">
              <circle cx="120" cy="120" r={radius} fill="none" stroke="#1E2F4A" strokeWidth="28" data-id="element-628" data-mp="3enoowis59g2g" />
              <motion.circle cx="120" cy="120" r={radius} fill="none" stroke="#00D4AA" strokeWidth="28" strokeLinecap="round" strokeDasharray={circumference} style={{
              strokeDashoffset: dashOffset
            }} transform="rotate(-90 120 120)" data-id="element-629" data-mp="1p6azx3iuhfrq" />
              <circle cx="120" cy="120" r={radius} fill="none" stroke="#00D4AA" strokeWidth="2" strokeOpacity="0.15" data-id="element-630" data-mp="2cyi3onqwgp16" />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center" data-id="element-631" data-mp="23zeine869zk1">
              <motion.div className="text-4xl md:text-5xl font-serif text-teal-400" data-id="element-632" data-mp="nhqrjarctzl8">
                {displayPct}
              </motion.div>
              <div className="text-white/50 text-xs uppercase tracking-widest font-medium mt-1" data-id="element-633" data-mp="2u712fp2v7san">
                Mature / Legacy
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      <div className="space-y-8" data-id="element-634" data-mp="2kgppxe548as5">
        <motion.div initial={{
        opacity: 0,
        x: 20
      }} animate={{
        opacity: stage >= 2 ? 1 : 0,
        x: stage >= 2 ? 0 : 20
      }} className="bg-navy-900/50 border border-navy-700 rounded-2xl p-8" data-id="element-635" data-mp="2kfy0ym60xbau">
          <div className="flex items-start gap-4" data-id="element-636" data-mp="26g574hz7aeyn">
            <div className="w-12 h-12 rounded-full bg-teal-400/10 flex items-center justify-center flex-shrink-0" data-id="element-637" data-mp="21idhjum134xm">
              <TrendingDown className="w-6 h-6 text-teal-400" data-id="element-638" data-mp="1xhtj8nppxv9w" />
            </div>
            <div data-id="element-639" data-mp="3vwebq26mv3rd">
              <h3 className="text-3xl font-serif text-white mb-2" data-id="element-640" data-mp="3vwxwdqno4693">
                $180-300B+
              </h3>
              <p className="text-white/70 leading-relaxed" data-id="element-641" data-mp="3rwdygkv9sylw">
                Revenue facing Loss of Exclusivity (LoE) in the coming years.
                This massive pool of capital is transitioning into the legacy
                phase.
              </p>
            </div>
          </div>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        x: 20
      }} animate={{
        opacity: stage >= 3 ? 1 : 0,
        x: stage >= 3 ? 0 : 20
      }} className="bg-navy-900/50 border border-navy-700 rounded-2xl p-8" data-id="element-642" data-mp="1ndmvpokraxey">
          <div className="flex items-start gap-4" data-id="element-643" data-mp="zobt01juisy6">
            <div className="w-12 h-12 rounded-full bg-purple-500/10 flex items-center justify-center flex-shrink-0" data-id="element-644" data-mp="13pdr2f210yyk">
              <PieChart className="w-6 h-6 text-purple-400" data-id="element-645" data-mp="18ogy251khf96" />
            </div>
            <div data-id="element-646" data-mp="2f4j86o2wn01p">
              <h3 className="text-xl font-bold text-white mb-2" data-id="element-647" data-mp="2f52et8n3u20f">
                The Unmanaged Majority
              </h3>
              <p className="text-white/70 leading-relaxed" data-id="element-648" data-mp="2k22jpg87wypy">
                While 90% of marketing budget goes to the top 20% of products,
                the remaining 80% of the portfolio is left to erode naturally
                without support.
              </p>
            </div>
          </div>
        </motion.div>

        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: stage >= 4 ? 1 : 0
      }} className="pt-4" data-id="element-649" data-mp="24kezw89798lo"></motion.div>
      </div>

      <motion.div initial={{
      opacity: 0,
      y: 10
    }} animate={{
      opacity: stage >= 4 ? 1 : 0,
      y: stage >= 4 ? 0 : 10
    }} className="mt-8 flex items-center justify-between gap-6 bg-teal-400/5 border border-teal-400/20 rounded-xl px-6 py-4" data-id="element-650" data-mp="2ej4jc5gqv141">
        <p className="text-white/80 leading-relaxed max-w-3xl" data-id="element-651" data-mp="t3oxz1bt72xv">
          These brands represent a{' '}
          <span className="text-teal-400 font-semibold" data-id="element-652" data-mp="p2vbmce0lw73">
            massive, under-optimized segment
          </span>{' '}
          of pharma portfolios — the part that quietly funds the next generation
          of innovation.
        </p>
      </motion.div>
    </div>;
}